import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import { getOrderNextAction } from '@hero/core';
import { StatusBadgeConfigType, TableCell } from '@hero/web-components';
import { useFeature } from '@hero/web-context';
import { ZapIcon } from '@hero/web-icons';
import { OrderStatusBadge } from '../../Status/OrderStatusBadge';
import { OrderUpdateStatusModal } from '../../OrderActions/OrderUpdateStatusModal';

export interface StatusCellProps {
  order: Order;
  displayForUser: 'customer' | 'carrier';
  statusConfig?: StatusBadgeConfigType;
  className?: string;
}

/**
 * Status column cell — a single-line status badge. When the viewer is
 * a carrier who can advance the order (there IS a next step and they
 * hold `sub_edit_order`), the badge becomes a button: hovering swaps
 * it for the next-action chip ("Start Journey", "Mark Delivered", …)
 * and clicking opens the same `OrderUpdateStatusModal` that powers
 * the drawer actions menu, so the confirmation flow, image upload,
 * and ref-number fields stay identical. Terminal statuses (delivered
 * / canceled) have no next step, so they render as a plain badge.
 */
export const StatusCell: React.FC<StatusCellProps> = ({
  order,
  displayForUser,
  statusConfig,
  className,
}) => {
  const { t } = useTranslation('m-order');
  const { isEnabled: canEditOrder } = useFeature('order_module', 'sub_edit_order');
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);

  const nextStep = getOrderNextAction(order.status.order);
  const nextStatusLabel = t(`actions.order_action_labels.${order.status.order}`, {
    defaultValue: '',
  });
  const showUpdateStatus = displayForUser === 'carrier' && !!nextStep && canEditOrder;

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const openUpdateStatus = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUpdateStatusOpen(true);
  };

  const badge = (
    <OrderStatusBadge
      status={order.status.order}
      statusConfig={statusConfig}
      displayForUser={displayForUser}
    />
  );

  if (!showUpdateStatus) {
    return <TableCell className={className}>{badge}</TableCell>;
  }

  return (
    <TableCell className={className} onClick={stop}>
      <button
        type="button"
        onClick={openUpdateStatus}
        title={nextStatusLabel}
        aria-label={nextStatusLabel}
        className="group/status -mx-2 -my-1 inline-flex min-h-[2.75rem] min-w-[8.5rem] items-center justify-start rounded-lg px-2.5 py-2 transition-colors duration-200 ease-out hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
        {/* Grid stack — sizes to the wider label; crossfade on hover. */}
        <span className="relative inline-grid grid-cols-1">
          <span className="col-start-1 row-start-1 inline-flex transition-all duration-200 ease-out group-hover/status:scale-[0.97] group-hover/status:opacity-0 group-focus-visible/status:scale-[0.97] group-focus-visible/status:opacity-0">
            {badge}
          </span>
          <span className="col-start-1 row-start-1 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary whitespace-nowrap opacity-0 scale-[0.97] transition-all duration-200 ease-out group-hover/status:scale-100 group-hover/status:opacity-100 group-focus-visible/status:scale-100 group-focus-visible/status:opacity-100">
            <ZapIcon className="!h-5 !w-5 shrink-0" />
            {nextStatusLabel}
          </span>
        </span>
      </button>
      <OrderUpdateStatusModal
        order={order}
        isOpen={isUpdateStatusOpen}
        onClose={() => setIsUpdateStatusOpen(false)}
      />
    </TableCell>
  );
};
